import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TaskItemView } from '../../api/generated/models/TaskItemView';
import type { TaskBoardStatus } from '../../utils/taskHelpers';
import TaskCard from './TaskCard';

interface TaskColumnProps {
  status: TaskBoardStatus;
  tasks: TaskItemView[];
  onTaskClick: (taskId: string) => void;
  canDrag?: boolean;
  draggingTaskId: string | null;
  isDragOver: boolean;
  onDragStart: (taskId: string) => void;
  onDragEnd: () => void;
  onDragEnter: (status: TaskBoardStatus) => void;
  onDragLeave: (status: TaskBoardStatus) => void;
  onDrop: (status: TaskBoardStatus, taskId: string) => void;
}

const TaskColumn: React.FC<TaskColumnProps> = ({
  status,
  tasks,
  onTaskClick,
  canDrag = true,
  draggingTaskId,
  isDragOver,
  onDragStart,
  onDragEnd,
  onDragEnter,
  onDragLeave,
  onDrop,
}) => {
  const { t } = useTranslation();

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    onDragEnter(status);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null;
    if (nextTarget && event.currentTarget.contains(nextTarget)) {
      return;
    }
    onDragLeave(status);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const taskId = event.dataTransfer.getData('text/plain');
    onDrop(status, taskId);
  };

  return (
    <section className="tasks-column">
      <header className="tasks-column-header">
        <h2 className="tasks-column-title">{t(`tasks_status_${status}`)}</h2>
        <span className="tasks-column-count">{tasks.length}</span>
      </header>
      <div
        className={`tasks-column-body${isDragOver ? ' tasks-column-body--drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onClick={onTaskClick}
            draggable={canDrag}
            isDragging={draggingTaskId === task.id}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        ))}
      </div>
    </section>
  );
};

export default TaskColumn;
